import { Link, useLocation } from '@tanstack/react-router';
import {
  BarChart3,
  BookOpen,
  Building,
  Building2,
  Eye,
  LogOut,
  Shield,
  ShieldCheck,
  Ticket,
  User,
  Users,
  X,
} from 'lucide-react';

import { Button } from '@frontend/components/ui/button';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@frontend/components/ui/sidebar';
import { useCurrentUser } from '@frontend/features/auth/hooks/useCurrentUser';
import { usePermissions } from '@frontend/features/auth/hooks/usePermissions';
import { logout } from '@frontend/features/auth/util/util.auth.logout';

const navigation = [
  {
    name: 'Component Catalog',
    href: '/component-catalog',
    icon: BookOpen,
  },
  {
    name: 'Provider Network',
    href: '/provider-network',
    icon: Building2,
    subItems: [
      {
        name: 'Groups',
        href: '/provider-network/groups',
        icon: Users,
      },
      {
        name: 'Clinics',
        href: '/provider-network/clinics',
        icon: Building,
      },
      {
        name: 'Providers',
        href: '/provider-network/providers',
        icon: User,
      },
    ],
  },
  {
    name: 'Ticketing',
    href: '/ticketing',
    icon: Ticket,
  },
  {
    name: 'Analytics',
    href: '/analytics',
    icon: BarChart3,
  },
];

export function AppSidebar() {
  const location = useLocation();
  const { toggleSidebar } = useSidebar();
  const { user, isLoading } = useCurrentUser();
  const { role, isViewOnly } = usePermissions();

  const getRoleIcon = () => {
    if (role === 'Admin') return <ShieldCheck className="h-3 w-3" />;
    if (role === 'PNOps') return <Shield className="h-3 w-3" />;
    return <Eye className="h-3 w-3" />;
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <Sidebar>
      <SidebarHeader>
        <div className="flex items-center justify-between h-16 px-6">
          <h1 className="text-xl font-semibold">K2 System</h1>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className="h-6 w-6"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close sidebar</span>
          </Button>
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navigation.map((item) => {
                const isActive = location.pathname.startsWith(item.href);

                if (item.subItems) {
                  return (
                    <SidebarMenuItem key={item.name}>
                      <SidebarMenuButton asChild isActive={isActive}>
                        <Link to={item.href}>
                          <item.icon />
                          <span>{item.name}</span>
                        </Link>
                      </SidebarMenuButton>
                      <SidebarMenuSub>
                        {item.subItems.map((subItem) => {
                          const isSubActive =
                            location.pathname === subItem.href;
                          return (
                            <SidebarMenuSubItem key={subItem.name}>
                              <SidebarMenuSubButton
                                asChild
                                isActive={isSubActive}
                              >
                                <Link to={subItem.href}>
                                  <subItem.icon />
                                  <span>{subItem.name}</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          );
                        })}
                      </SidebarMenuSub>
                    </SidebarMenuItem>
                  );
                }

                return (
                  <SidebarMenuItem key={item.name}>
                    <SidebarMenuButton asChild isActive={isActive}>
                      <Link to={item.href}>
                        <item.icon />
                        <span>{item.name}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {!isLoading && user && (
                <>
                  <SidebarMenuItem>
                    <div className="flex items-start gap-2 px-2 py-2">
                      <User className="h-4 w-4 flex-shrink-0" />
                      <div className="flex flex-col items-start gap-0.5 min-w-0 flex-1">
                        <span className="text-sm font-medium truncate w-full">
                          {user.name}
                        </span>
                        {role && (
                          <span
                            className={`text-xs flex items-center gap-1 ${isViewOnly ? 'text-yellow-600' : 'text-blue-600'}`}
                          >
                            {getRoleIcon()}
                            <span>
                              {role}
                              {isViewOnly && ' (View Only)'}
                            </span>
                          </span>
                        )}
                      </div>
                    </div>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton onClick={handleLogout}>
                      <LogOut className="h-4 w-4" />
                      <span>Logout</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarFooter>
    </Sidebar>
  );
}
